import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type IValidator = ReturnType<typeof useListValidator>;
export interface IValidationItem {
  id: string; // unique id
  required?: boolean; // is required
  isValid?: boolean; // is valid
  errorMessage?: string; // error message
  isLoading?: boolean; // Used if a validated element is awaiting a loading transition
  data?: any; // *Deprecated* data to store with the validatee
}

export type IValidatorErrorStates = { isValid: boolean; isError: boolean; errorMessage: string };

export type IValidateFromObject = {
  [x: string]: IValidatorErrorStates;
};

type InitializerValidatorSelfReturn = {
  errorStates: IValidatorErrorStates;
  setErrorStates: React.Dispatch<React.SetStateAction<IValidatorErrorStates>>;
  setIsLoading: (isLoading: boolean) => void;
  setIsRequired: (isRequired: boolean) => void;
  validateFromObjectEntries: (object: IValidateFromObject) => IValidatorErrorStates;
};

const useInitializeSelf = (
  validator: {
    subscribe: (self: IValidationItem) => void;
    update: (self: IValidationItem) => void;
  },
  IValidationItem: IValidationItem
): InitializerValidatorSelfReturn => {
  // Manage per-component loading State
  const [isComponentLoading, setIsComponentLoading] = useState(false);
  // Easily change required Status
  const [isRequired, setIsRequired] = useState(IValidationItem.required);
  // Contain the Validation Items error states
  const [errorStates, setErrorStates] = useState({
    isValid: true,
    isError: false,
    errorMessage: '',
  });

  // Abstracted for semantics
  const setIsLoading = (isLoading: boolean) => {
    setIsComponentLoading(isLoading);
  };

  const validateFromObjectEntries = useCallback((object: IValidateFromObject) => {
    const isAllValid = !Object.entries(object).some(([, value]) => value.isValid === false);
    const hasError = Object.entries(object).find(([, value]) => {
      return value.isError;
    });
    return {
      isValid: isAllValid,
      isError: !!hasError,
      errorMessage: hasError ? hasError[1].errorMessage : '',
    };
  }, []);

  // Define thy Self
  const self = useMemo(
    () => ({
      id: IValidationItem.id,
      required: isRequired,
      isValid: errorStates.isValid,
      errorMessage: errorStates.errorMessage,
      isLoading: isComponentLoading,
    }),
    [errorStates, isComponentLoading, isRequired]
  );

  useEffect(() => {
    return validator.subscribe?.(self);
  }, []);

  useEffect(() => {
    validator?.update(self);
  }, [self]);

  return { errorStates, setErrorStates, setIsLoading, setIsRequired, validateFromObjectEntries };
};

const useInitializeSelfAsValidator = (validator: {
  subscribeAsValidator: (elements: any) => void;
}): {
  isValid: boolean;
  isLoading: boolean;
  errorMessages: string[] | undefined;
  invalidElements: string[] | undefined;
} => {
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState(['']);
  const [invalidElements, setInvalidElements] = useState<string[] | undefined>();
  const validateAllElements = useCallback((elements) => {
    let isInvalid = false;
    let isLoading = false;
    let hasErrorMessage = [''];
    let invalidElementList: string[] = [];
    elements.forEach((element: IValidationItem) => {
      if (element.required && !element.isValid) {
        isInvalid = true;
        invalidElementList.push(element.id);
      }
      // If any element is in some loading state, let the validator know
      if (element.isLoading) {
        isLoading = true;
      }
      if (element.errorMessage) {
        hasErrorMessage.push(element.errorMessage);
      }
    });
    setErrorMessages(hasErrorMessage);
    setInvalidElements(invalidElementList);
    setIsLoading(isLoading);
    setIsValid(!isInvalid);
  }, []);

  useEffect(() => {
    return validator.subscribeAsValidator(validateAllElements);
  }, []);

  return { isValid, isLoading, errorMessages, invalidElements };
};

export const useListValidator = () => {
  // list of validation items
  const validationList = useRef(new Map());
  const validators = useRef(new Set());

  const update = useCallback((self: IValidationItem) => {
    validationList.current.set(self.id, self);
    validators.current.forEach((callback: any) => callback(validationList.current));
  }, []);

  // Subscribe to validation list to be validated against
  const subscribe = useCallback((self: IValidationItem) => {
    validationList.current.set(self.id, self);
    return () => {
      validationList.current.delete(self.id);
      // Notify each validator that the listSize has changed
      validators.current.forEach((callback: any) => callback(validationList.current));
    };
  }, []);

  // Subscribe to validator validation list to be notified of changes
  const subscribeAsValidator = useCallback((callback): any => {
    // When using callback, it should be some kind of memoized function
    validators.current.add(callback);
    return () => {
      // Remove self from the list of validators, so it won't be notified anymore
      validators.current.delete(callback);
    };
  }, []);

  const initializeValidatee = useCallback(
    (IValidationItem: IValidationItem) => useInitializeSelf({ subscribe, update }, IValidationItem),
    []
  );

  const initializeValidator = useCallback(
    () => useInitializeSelfAsValidator({ subscribeAsValidator }),
    []
  );

  return {
    subscribe, // deprecate return
    update, // deprecate return
    subscribeAsValidator: initializeValidator,
    set: initializeValidatee,
  };
};
